import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from 'aws-blocks';
import BkButton from '../../Components/ui/BkButton';
import BkPage from '../../Components/ui/BkPage';
import BkPanel from '../../Components/ui/BkPanel';
import AgeConfirm from '../../Components/AgeConfirm';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSnackBarContext } from '../../contexts/SnackBarContext';
import usePageMeta from '../../hooks/use-page-meta';
import { formatCurrencyAmount } from '../../utils/format-currency';
import StoreItemModal from './StoreItemModal';
import StoreItemTile, { type StoreItem } from './StoreItemTile';
import { STORE_CATEGORIES, useStoreCatalog } from './store-catalog';

type PassSummary = {
  season: {
    seasonId: string;
    name: string;
    active: boolean;
    priceGems: number;
    priceMoney: number;
  };
  progress: {
    premium: boolean;
  };
};

export default function StoreHub() {
  usePageMeta({
    title: 'Store',
    description:
      'Buy gems, skins, profile icons, and emotes for Blind Kings.',
    path: '/store',
  });

  const { featured, byCategory, loading } = useStoreCatalog();
  const [selected, setSelected] = useState<StoreItem | undefined>();
  const [pass, setPass] = useState<PassSummary | null>(null);
  const [passBusy, setPassBusy] = useState(false);
  const [confirm18, setConfirm18] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser, queryUserData, userData, openLoginModal } =
    useAuthContext();
  const { addSnackBar } = useSnackBarContext();
  const gems = Number(userData?.gems ?? 0);

  const loadPass = useCallback(() => {
    if (!currentUser) {
      setPass(null);
      return;
    }
    void api
      .getBattlePass()
      .then((result: PassSummary) => setPass(result))
      .catch(() => setPass(null));
  }, [currentUser]);

  useEffect(() => {
    loadPass();
  }, [loadPass]);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    const sessionId = searchParams.get('session_id');
    if (!checkout) return;

    const clearParams = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('checkout');
      next.delete('session_id');
      setSearchParams(next, { replace: true });
    };

    if (checkout === 'cancel') {
      addSnackBar({
        title: 'Checkout canceled',
        message: 'No charge was made.',
        variant: 'info',
        timeout: 2500,
      });
      clearParams();
      return;
    }

    if (checkout === 'success' && sessionId && currentUser) {
      void (async () => {
        try {
          const result = (await api.confirmGemCheckout(sessionId)) as {
            alreadyFulfilled?: boolean;
            player?: { gems?: number };
          };
          await queryUserData();
          addSnackBar({
            title: result.alreadyFulfilled ? 'Gems ready' : 'Gems added',
            message: result.alreadyFulfilled
              ? 'Your gem balance is up to date.'
              : `Your new balance is ${formatCurrencyAmount(result.player?.gems ?? 0)} gems.`,
            variant: 'success',
            timeout: 3500,
          });
        } catch (err) {
          addSnackBar({
            title: 'ERROR',
            message:
              err instanceof Error
                ? err.message
                : 'Could not confirm gem purchase.',
            variant: 'danger',
            timeout: 4000,
          });
        } finally {
          clearParams();
        }
      })();
    } else if (checkout === 'success' && !currentUser) {
      addSnackBar({
        title: 'Log in',
        message: 'Sign in to claim your gem purchase.',
        variant: 'info',
        timeout: 3500,
      });
    }
  }, [
    searchParams,
    setSearchParams,
    currentUser,
    queryUserData,
    addSnackBar,
  ]);

  async function buyPassPremium(method: 'gems' | 'money') {
    if (!currentUser) {
      openLoginModal();
      return;
    }
    if (method === 'money' && !confirm18) {
      addSnackBar({
        title: 'Age required',
        message: 'Confirm you are 18 or older before paying with a card.',
        variant: 'warning',
        timeout: 2500,
      });
      return;
    }
    setPassBusy(true);
    try {
      if (method === 'money') {
        const result = (await api.buyBattlePassPremium('money')) as {
          url?: string;
        };
        if (result.url) {
          window.location.assign(result.url);
          return;
        }
        throw new Error('No checkout URL');
      }
      await api.buyBattlePassPremium('gems');
      await queryUserData();
      loadPass();
      addSnackBar({
        title: 'Premium unlocked',
        message:
          'Season premium track is yours. Claim rewards on the Pass page.',
        variant: 'success',
        timeout: 3500,
      });
    } catch (err) {
      addSnackBar({
        title: 'ERROR',
        message: err instanceof Error ? err.message : String(err),
        variant: 'danger',
        timeout: 3500,
      });
    } finally {
      setPassBusy(false);
    }
  }

  const season = pass?.season;
  const premiumOwned = Boolean(pass?.progress?.premium);
  const canBuyPass =
    !premiumOwned && (!currentUser || Boolean(season?.active));
  const priceGems = Number(season?.priceGems ?? 900);
  const priceMoney = Number(season?.priceMoney ?? 9.99);
  const seasonName = season?.name ?? 'Season 1 — Crownfall';

  return (
    <BkPage>
      <div className="mb-8 space-y-2">
        <h1 className="text-2xl font-bold uppercase tracking-wider text-bk-gold md:text-3xl">
          Store
        </h1>
        <p className="max-w-2xl text-sm text-bk-muted md:text-base">
          Stock up and dress your army. Seasonal exclusives land on the Battle
          Pass first — then return here as legacy after the season ends.
        </p>
      </div>

      <div className="space-y-10">
        <section className="space-y-3">
          <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-bk-gold">
            Featured
          </h2>
          <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <BkPanel className="flex flex-col justify-between gap-4 border-bk-gold/40 bg-linear-to-br from-bk-gold/10 to-transparent">
              <div className="space-y-1">
                <p className="text-[11px] font-bold uppercase tracking-wider text-bk-gold">
                  Seasonal
                </p>
                <h3 className="text-xl font-extrabold uppercase tracking-wide text-bk-text">
                  {seasonName}
                </h3>
                <p className="max-w-md text-sm text-bk-muted">
                  Free + premium tracks. Icons, emotes, gems, and boosts —
                  unlock premium here or climb for free rewards on the Pass.
                </p>
                {premiumOwned ? (
                  <p className="pt-1 text-xs font-bold uppercase tracking-wide text-bk-gold">
                    Premium owned
                  </p>
                ) : !season?.active && currentUser ? (
                  <p className="pt-1 text-xs text-bk-muted">
                    Premium buy is available while the season is active.
                  </p>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {canBuyPass ? (
                  <>
                    <BkButton
                      variant="primary"
                      disabled={
                        passBusy ||
                        (Boolean(currentUser) && gems < priceGems)
                      }
                      onClick={() => void buyPassPremium('gems')}
                    >
                      Buy · {formatCurrencyAmount(priceGems)} gems
                    </BkButton>
                    <BkButton
                      variant="secondary"
                      disabled={passBusy || !confirm18}
                      onClick={() => void buyPassPremium('money')}
                    >
                      Buy · ${priceMoney.toFixed(2)}
                    </BkButton>
                  </>
                ) : null}
                <Link to="/pass">
                  <BkButton variant="ghost">
                    {premiumOwned ? 'View Pass' : 'Open Battle Pass'}
                  </BkButton>
                </Link>
              </div>
              {canBuyPass ? (
                <AgeConfirm
                  id="store-pass-age-18"
                  checked={confirm18}
                  onChange={setConfirm18}
                >
                  I confirm I am 18 or older and I am buying the premium track
                  for myself.
                </AgeConfirm>
              ) : null}
            </BkPanel>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              {featured.gemHighlight ? (
                <StoreItemTile
                  item={featured.gemHighlight}
                  badge="Popular"
                  onClick={setSelected}
                />
              ) : loading ? (
                <BkPanel className="text-sm text-bk-muted">Loading…</BkPanel>
              ) : null}
              {featured.cosmetics[0] ? (
                <StoreItemTile
                  item={featured.cosmetics[0]}
                  onClick={setSelected}
                />
              ) : null}
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-bk-gold">
            Browse
          </h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
            {STORE_CATEGORIES.map((cat) => {
              const count =
                cat.id === 'boards'
                  ? byCategory.boards.length
                  : byCategory[cat.id].length;
              return (
                <Link
                  key={cat.id}
                  to={cat.path}
                  className="group border border-bk-border bg-bk-panel p-4 transition-colors hover:border-bk-gold"
                >
                  <div className="text-lg font-bold uppercase tracking-wide text-bk-text group-hover:text-bk-gold-bright">
                    {cat.label}
                  </div>
                  <p className="mt-1 text-xs text-bk-muted">{cat.blurb}</p>
                  <p className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-bk-gold">
                    {count} item{count === 1 ? '' : 's'} →
                  </p>
                </Link>
              );
            })}
          </div>
        </section>

        {featured.cosmetics.length > 1 ? (
          <section className="space-y-3">
            <div className="flex items-end justify-between gap-2">
              <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-bk-gold">
                Spotlight
              </h2>
              <Link
                to="/store/icons"
                className="text-xs font-semibold uppercase tracking-wide text-bk-muted hover:text-bk-gold"
              >
                View all icons
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              {featured.cosmetics.map((item) => (
                <StoreItemTile
                  key={String(item.itemId)}
                  item={item}
                  onClick={setSelected}
                />
              ))}
            </div>
          </section>
        ) : null}
      </div>

      <StoreItemModal
        selectedItem={selected}
        onClose={() => setSelected(undefined)}
      />
    </BkPage>
  );
}
